/* Browser-local history only. Cards are rendered by Shopify, never cached prices. */
(() => {
  if (window.__norgiraRecentlyViewedReady) return;
  window.__norgiraRecentlyViewedReady = true;
  const KEY = 'norgira:recently-viewed:v1';
  const MAX_HISTORY = 20, MAX_VISIBLE = 10, MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  function normalizeHistory(value, now = Date.now()) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.filter(item => {
      if (!item || !validHandle(item.handle) || !Number.isFinite(item.viewedAt) || item.viewedAt > now || now - item.viewedAt > MAX_AGE || seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    }).sort((a, b) => b.viewedAt - a.viewedAt).slice(0, MAX_HISTORY).map(({ handle, viewedAt }) => ({ handle, viewedAt }));
  }
  function remember(history, handle, now = Date.now()) {
    if (!validHandle(handle)) return normalizeHistory(history, now);
    return normalizeHistory([{ handle, viewedAt: now }, ...history.filter(item => item?.handle !== handle)], now);
  }
  function historyFor(history, current) {
    return history.filter(item => item.handle !== current).slice(0, MAX_HISTORY);
  }
  function readHistory() {
    try { return normalizeHistory(JSON.parse(localStorage.getItem(KEY) || '[]')); } catch (_) { return []; }
  }
  function saveHistory(history) {
    try { localStorage.setItem(KEY, JSON.stringify(history)); } catch (_) { /* Storage denied: retain the current-product fallback. */ }
  }
  const revisions = new WeakMap();
  async function render(root, recordVisit = false) {
    const handle = root.dataset.currentHandle;
    if (!validHandle(handle)) return;
    const revision = (revisions.get(root) || 0) + 1;
    revisions.set(root, revision);
    const history = readHistory();
    if (recordVisit) saveHistory(remember(history, handle));
    const candidates = historyFor(history, handle);
    const list = root.querySelector('[data-recent-list]');
    if (!list) return;
    const present = cards => {
      const fallback = root.querySelector('template[data-recent-card]')?.content.querySelector('[data-recent-handle]');
      const visible = cards.length ? cards.slice(0, MAX_VISIBLE) : fallback ? [document.importNode(fallback, true)] : [];
      list.replaceChildren(...visible);
      root.hidden = visible.length === 0;
      root.dataset.recentMode = cards.length ? 'history' : 'current-product';
      root.removeAttribute('aria-busy');
      if (visible.length) document.dispatchEvent(new CustomEvent('norgira:recently-viewed-rendered'));
    };
    if (!candidates.length) { present([]); return; }
    root.setAttribute('aria-busy', 'true');
    const loadCard = async candidate => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 7000);
      try {
        const url = new URL(`${root.dataset.rootUrl || '/'}products/${encodeURIComponent(candidate.handle)}`, location.origin);
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) return null;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = parsed.querySelector('template[data-recent-card]');
        if (!template || template.dataset.productHandle !== candidate.handle) return null;
        const card = template.content.querySelector('[data-recent-handle]');
        return card ? document.importNode(card, true) : null;
      } catch (_) { return null; } finally { clearTimeout(timeout); }
    };
    // Four in parallel; preserve browsing order, and continue past deleted products.
    const cards = [];
    for (let offset = 0; offset < candidates.length && cards.length < MAX_VISIBLE; offset += 4) {
      cards.push(...(await Promise.all(candidates.slice(offset, offset + 4).map(loadCard))).filter(Boolean));
      if (revisions.get(root) !== revision || !root.isConnected) return;
    }
    if (revisions.get(root) !== revision) return;
    // Existing card observers bind wishlist, sizes, image arrows and carousel controls.
    present(cards);
  }
  function setup() {
    document.querySelectorAll('[data-recently-viewed]').forEach(root => {
      if (root.dataset.recentReady) return;
      root.dataset.recentReady = 'true';
      render(root, true);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
  document.addEventListener('shopify:section:load', setup);
  window.addEventListener('pageshow', event => {
    if (event.persisted) document.querySelectorAll('[data-recently-viewed]').forEach(root => render(root, true));
  });
  window.addEventListener('storage', event => {
    if (event.key === KEY) document.querySelectorAll('[data-recently-viewed]').forEach(root => render(root));
  });
})();
